import React, { useEffect, useMemo, useState } from "react";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { firebaseApp } from "../../firebase/firebaseApp";

const PAGE_SIZE = 50;
const PLANS = ["free", "starter", "performance"];

function safeJsonStringify(v) {
  try {
    return JSON.stringify(v ?? {}, null, 2);
  } catch {
    return "{}";
  }
}

export default function AdminUsersPage() {
  const db = useMemo(() => getFirestore(firebaseApp), []);
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");

  const [edit, setEdit] = useState({
    open: false,
    uid: null,
    email: "",
    mode: "plan", // "plan" | "json"
    plan: "free",
    jsonText: "{}",
    error: "",
  });

  async function loadFirst() {
    setLoading(true);
    setCursor(null);
    setHasMore(true);

    // Order by email for predictability (works even if onboarding fields missing)
    const q = query(
      collection(db, "lungform_users"),
      orderBy("email", "asc"),
      limit(PAGE_SIZE)
    );

    const snap = await getDocs(q);
    const docs = snap.docs;

    setRows(docs.map((d) => ({ uid: d.id, ...d.data() })));
    setCursor(docs[docs.length - 1] || null);
    setHasMore(docs.length === PAGE_SIZE);
    setLoading(false);
  }

  async function loadNext() {
    if (!cursor || !hasMore) return;
    setLoadingMore(true);

    const q = query(
      collection(db, "lungform_users"),
      orderBy("email", "asc"),
      startAfter(cursor),
      limit(PAGE_SIZE)
    );

    const snap = await getDocs(q);
    const docs = snap.docs;

    setRows((p) => [...p, ...docs.map((d) => ({ uid: d.id, ...d.data() }))]);
    setCursor(docs[docs.length - 1] || null);
    setHasMore(docs.length === PAGE_SIZE);
    setLoadingMore(false);
  }

  useEffect(() => {
    loadFirst().catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      String(r.email || "")
        .toLowerCase()
        .includes(s)
    );
  }, [rows, search]);

  async function saveEdit() {
    const uid = edit.uid;
    if (!uid) return;

    const ref = doc(db, "lungform_users", uid);

    try {
      if (edit.mode === "plan") {
        const plan = String(edit.plan || "").toLowerCase();
        if (!PLANS.includes(plan)) {
          setEdit((p) => ({ ...p, error: "Invalid plan value." }));
          return;
        }

        await updateDoc(ref, {
          "entitlements.plan": plan,
        });

        setRows((prev) =>
          prev.map((r) =>
            r.uid === uid
              ? { ...r, entitlements: { ...(r.entitlements || {}), plan } }
              : r
          )
        );
      } else {
        // Full overwrite of entitlements map from JSON text
        const parsed = JSON.parse(edit.jsonText || "{}");
        if (
          typeof parsed !== "object" ||
          parsed === null ||
          Array.isArray(parsed)
        ) {
          setEdit((p) => ({
            ...p,
            error: "Entitlements JSON must be an object.",
          }));
          return;
        }

        await updateDoc(ref, { entitlements: parsed });

        setRows((prev) =>
          prev.map((r) => (r.uid === uid ? { ...r, entitlements: parsed } : r))
        );
      }

      setEdit({
        open: false,
        uid: null,
        email: "",
        mode: "plan",
        plan: "free",
        jsonText: "{}",
        error: "",
      });
    } catch (e) {
      setEdit((p) => ({ ...p, error: e?.message || "Update failed." }));
    }
  }

  return (
    <div className="w-full max-w-6xl p-4 space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="title-text">Admin · Users</h1>
          <p className="muted-text">Collection: lungform_users</p>
        </div>

        <div className="flex gap-2 w-full md:w-auto">
          <input
            className="custom-input-on-dark w-full md:w-96"
            placeholder="Search by email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="outlined-button" onClick={loadFirst}>
            Refresh
          </button>
        </div>
      </div>

      <div className="rounded-2xl border-2 border-greyscale-1 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-container text-xs font-semibold">
          <div className="col-span-4">Email</div>
          <div className="col-span-4">UID</div>
          <div className="col-span-3">Entitlements.plan</div>
          <div className="col-span-1 text-right">Edit</div>
        </div>

        {loading ? (
          <div className="p-4">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-4">No users.</div>
        ) : (
          filtered.map((r) => (
            <div
              key={r.uid}
              className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-greyscale-1 items-center text-sm"
            >
              <div className="col-span-4 truncate">{r.email || "—"}</div>
              <div className="col-span-4 font-mono truncate">{r.uid}</div>
              <div className="col-span-3">
                {String(r.entitlements?.plan || "—")}
              </div>
              <div className="col-span-1 text-right">
                <button
                  className="small-outlined-button"
                  onClick={() =>
                    setEdit({
                      open: true,
                      uid: r.uid,
                      email: r.email || "",
                      mode: "plan",
                      plan: String(
                        r.entitlements?.plan || "free"
                      ).toLowerCase(),
                      jsonText: safeJsonStringify(r.entitlements || {}),
                      error: "",
                    })
                  }
                >
                  Edit
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="muted-text">Loaded: {rows.length}</div>
        <button
          className="outlined-button"
          onClick={loadNext}
          disabled={!hasMore || loadingMore}
        >
          {loadingMore ? "Loading…" : hasMore ? "Load more" : "No more"}
        </button>
      </div>

      {edit.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-container rounded-2xl border-2 border-greyscale-1 w-full max-w-xl p-4 space-y-3">
            <div>
              <div className="subtitle-text">Edit entitlements</div>
              <div className="muted-text">{edit.email || edit.uid}</div>
            </div>

            <div className="flex gap-2">
              <button
                className={
                  edit.mode === "plan" ? "primary-button" : "outlined-button"
                }
                onClick={() =>
                  setEdit((p) => ({ ...p, mode: "plan", error: "" }))
                }
              >
                Plan only
              </button>
              <button
                className={
                  edit.mode === "json" ? "primary-button" : "outlined-button"
                }
                onClick={() =>
                  setEdit((p) => ({ ...p, mode: "json", error: "" }))
                }
              >
                Full JSON
              </button>
            </div>

            {edit.mode === "plan" ? (
              <div className="space-y-2">
                <div className="text-sm font-semibold">entitlements.plan</div>
                <select
                  className="custom-input-on-dark w-full"
                  value={edit.plan}
                  onChange={(e) =>
                    setEdit((p) => ({ ...p, plan: e.target.value }))
                  }
                >
                  {PLANS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-sm font-semibold">
                  entitlements (overwrite)
                </div>
                <textarea
                  className="custom-input-on-dark w-full font-mono text-xs"
                  rows={10}
                  value={edit.jsonText}
                  onChange={(e) =>
                    setEdit((p) => ({ ...p, jsonText: e.target.value }))
                  }
                />
                <div className="muted-text text-xs">
                  This overwrites the entire <code>entitlements</code> map.
                </div>
              </div>
            )}

            {edit.error ? (
              <div className="text-xs text-red-400">{edit.error}</div>
            ) : null}

            <div className="flex gap-2 justify-end pt-2">
              <button
                className="outlined-button"
                onClick={() =>
                  setEdit({
                    open: false,
                    uid: null,
                    email: "",
                    mode: "plan",
                    plan: "free",
                    jsonText: "{}",
                    error: "",
                  })
                }
              >
                Cancel
              </button>
              <button className="primary-button" onClick={saveEdit}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
